import type { BpaDocumentDto } from "../../../apis/dischargeService";
import type { StagedDocEntry } from "./useStageDocumentUploadPreview";
import { isMandatoryClaimDocumentLabel } from "./claimFieldUtils";

export { isOptionalClaimDocumentLabel } from "./claimFieldUtils";

export const getMissingMandatoryClaimDocuments = (
  labels: readonly string[],
  documents: BpaDocumentDto[] | undefined,
  stagedDocs: Record<string, StagedDocEntry>,
  typeMap: Record<string, string>,
  isIpVisit = false,
): string[] =>
  labels
    .filter((label) => isMandatoryClaimDocumentLabel(label, isIpVisit))
    .filter((label) => {
      if (stagedDocs[label]) return false;
      const serverDoc = findDocumentByLabel(documents, label, typeMap);
      return !serverDoc?.id;
    });

export const DISCHARGE_DOC_TYPES: Record<string, string> = {
  "Discharge Summary": "DISCHARGE_SUMMARY",
  "Geo Tag Photo": "GEO_TAG_PHOTO",
  "Feedback Form": "FEEDBACK_FORM",
};

export const DISCHARGE_MANDATORY_DOCUMENT_LABEL = "Discharge Summary";

export const isMandatoryDischargeDocumentLabel = (label: string): boolean =>
  label.trim().toLowerCase() === DISCHARGE_MANDATORY_DOCUMENT_LABEL.toLowerCase();

export const getMissingMandatoryDischargeDocuments = (
  documents: BpaDocumentDto[] | undefined,
  stagedDocs: Record<string, StagedDocEntry>,
  typeMap: Record<string, string>,
): string[] => {
  const label = DISCHARGE_MANDATORY_DOCUMENT_LABEL;
  if (stagedDocs[label]) return [];
  const serverDoc = findDocumentByLabel(documents, label, typeMap);
  return serverDoc?.id ? [] : [label];
};

export const CLAIM_DOC_TYPES: Record<string, string> = {
  "Consultation note": "CONSULTATION_NOTE",
  "consultation note": "CONSULTATION_NOTE",
  "Hospital bill": "HOSPITAL_BILL",
  "hospital bill": "HOSPITAL_BILL",
  "Investigation Report": "INVESTIGATION_REPORT",
  "investigation report": "INVESTIGATION_REPORT",
  "Pharmacy Bill": "PHARMACY_BILL",
  ICP: "ICP",
  "Implant Invoices": "IMPLANT_INVOICES",
  Pouches: "POUCHES",
  Other: "OTHER",
  other: "OTHER",
};

export const getClaimDocTypeMap = (isIpVisit: boolean): Record<string, string> => {
  if (!isIpVisit) {
    return {
      "Consultation note": "CONSULTATION_NOTE",
      "consultation note": "CONSULTATION_NOTE",
      "Hospital bill": "HOSPITAL_BILL",
      "hospital bill": "HOSPITAL_BILL",
      "Investigation Report": "INVESTIGATION_REPORT",
      "investigation report": "INVESTIGATION_REPORT",
      Other: "OTHER",
      other: "OTHER",
    };
  }
  return CLAIM_DOC_TYPES;
};

const DOC_LABEL_ALIASES: Record<string, string[]> = {
  "Discharge Summary": ["DISCHARGESUMMARY", "DISCHARGE"],
  "Geo Tag Photo": ["GEOTAGPHOTO", "GEOTAG", "DISCHARGEGEOTAGPHOTO"],
  "Feedback Form": ["FEEDBACKFORM", "DISCHARGEFEEDBACKFORM"],
  "Consultation note": ["CONSULTATIONNOTE", "CONSULTATION"],
  "consultation note": ["CONSULTATIONNOTE", "CONSULTATION"],
  "Hospital bill": ["HOSPITALBILL", "HOSPITALBILLDOCUMENT"],
  "hospital bill": ["HOSPITALBILL", "HOSPITALBILLDOCUMENT"],
  "Investigation Report": ["INVESTIGATIONREPORT", "INVESTIGATION"],
  "investigation report": ["INVESTIGATIONREPORT", "INVESTIGATION"],
  "Pharmacy Bill": ["PHARMACYBILL", "PHARMACY"],
  ICP: ["ICP", "INPATIENTCASEPAPER"],
  "Implant Invoices": ["IMPLANTINVOICES", "IMPLANT"],
  Pouches: ["POUCHES", "POUCH"],
  Other: ["OTHER", "CLAIMOTHER"],
  other: ["OTHER", "CLAIMOTHER"],
};

const normalizeDocKey = (value: string) =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const resolveLabelKey = (label: string, typeMap: Record<string, string>): string => {
  if (typeMap[label]) return label;
  const normalized = normalizeDocKey(label);
  const match = Object.keys(typeMap).find((key) => normalizeDocKey(key) === normalized);
  return match || label;
};

const docKeyMatchesLabel = (
  docTypeName: string,
  label: string,
  typeMap: Record<string, string>,
): boolean => {
  const docKey = normalizeDocKey(docTypeName || "");
  if (!docKey) return false;

  const resolvedLabel = resolveLabelKey(label, typeMap);
  const targetKey = normalizeDocKey(typeMap[resolvedLabel] || resolvedLabel);
  if (docKey === targetKey) return true;

  const aliases = DOC_LABEL_ALIASES[resolvedLabel] || DOC_LABEL_ALIASES[label] || [];
  if (aliases.some((alias) => docKey === alias || docKey.includes(alias) || alias.includes(docKey))) {
    return true;
  }

  return docKey.includes(targetKey) || targetKey.includes(docKey);
};

export const findDocumentByLabel = (
  documents: BpaDocumentDto[] | undefined,
  label: string,
  typeMap: Record<string, string>,
): BpaDocumentDto | undefined => {
  if (!documents?.length) return undefined;

  const matches = documents.filter((doc) =>
    docKeyMatchesLabel(doc.documentTypeName || "", label, typeMap),
  );
  if (!matches.length) return undefined;

  return matches.sort((a, b) => (b.id || 0) - (a.id || 0))[0];
};

export const findDocumentIdByLabel = (
  documents: BpaDocumentDto[] | undefined,
  label: string,
  typeMap: Record<string, string>,
) => findDocumentByLabel(documents, label, typeMap)?.id;

export const pickStageDocuments = (
  primary?: BpaDocumentDto[],
  fallback?: BpaDocumentDto[],
): BpaDocumentDto[] | undefined => {
  if (primary?.length) return primary;
  if (fallback?.length) return fallback;
  return primary ?? fallback;
};

export const downloadStagedFile = (entry: StagedDocEntry) => {
  const anchor = document.createElement("a");
  anchor.href = entry.url;
  anchor.download = entry.file.name;
  anchor.rel = "noopener noreferrer";
  anchor.click();
};
